import React, {useMemo, useEffect, useCallback} from 'react';
import {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Button, DateInput, Text, TextInput} from '@/ui-kits';
import {DataTableColumn, SpecialistPayments, GetSpecialistPaymentsRequestPayload} from '@/interface';
import {formatName} from '@/utils/common_functions';
import {NA} from '@/constants';
import {formatDate} from '@/utils/common_functions';
import {DataTable} from '@/components';
import {getSpecialistPaymentsRequest, resetSpecialistDashboardMessage} from '@/services/redux/slices/specialistDashboardSlice';
import {
  specialistPayments,
  specialistPaymentsLoading,
  specialistPaymentsCurrentPage,
  specialistPaymentsTotalPages,
  specialistPaymentsTotalResults,
  specialistPaymentsErrorMessage,
  specialistDashboardSuccessStatus,
} from '@/services/redux/selectors/specialistDashboardSelector';

export const SpecialistEarnings: React.FC = () => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState<string>('');
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);
  const [page, setPage] = useState<number>(1);
  const [isFilterActive, setIsFilterActive] = useState(false);

  // Redux selectors
  const data = useSelector(specialistPayments);
  const loading = useSelector(specialistPaymentsLoading);
  const totalPages = useSelector(specialistPaymentsTotalPages);
  const totalResult = useSelector(specialistPaymentsTotalResults);
  const currentPage = useSelector(specialistPaymentsCurrentPage);
  const errorMessage = useSelector(specialistPaymentsErrorMessage);
  const success = useSelector(specialistDashboardSuccessStatus);
  const error = useSelector(specialistPaymentsErrorMessage);

  const resetFilter = () => {
    setSearch('');
    setFrom(null);
    setTo(null);
    setPage(1);
    setIsFilterActive(false);
  };

  const fetchPayments = useCallback(() => {
    const payload: GetSpecialistPaymentsRequestPayload = {
      page,
    };

    let hasFilters = false;

    if (search) {
      payload.search_text = search;
      hasFilters = true;
    }

    if (from) {
      payload.start_date = from.toISOString().split('T')[0];
      hasFilters = true;
    }

    if (to) {
      payload.end_date = to.toISOString().split('T')[0];
      hasFilters = true;
    }

    setIsFilterActive(hasFilters);
    dispatch(getSpecialistPaymentsRequest(payload));
  }, [search, from, to, page, dispatch]);

  useEffect(() => {
    const timeout = setTimeout(fetchPayments, 500);
    return () => clearTimeout(timeout);
  }, [fetchPayments]);

  useEffect(() => {
    // Reset to first page when filters change
    return () => {
      dispatch(resetSpecialistDashboardMessage());
    };
  }, [success, error]);

  //------
  // table data
  //------
  const columns = useMemo(
    (): DataTableColumn<SpecialistPayments>[] => [
      {
        name: 'id',
        align: 'center',
        title: <Text>CASE ID</Text>,
        render: row => row.case_id,
        width: {minWidth: '150px'},
      },
      {
        name: 'patient_name',
        align: 'left',
        title: <Text>PATIENT NAME</Text>,
        render: row => formatName(row.patient_name),
        width: {minWidth: '200px'},
      },
      {
        name: 'consultation_date',
        align: 'center',
        title: <Text>CONSULTATION DATE</Text>,
        render: row => (row.consultation_date ? formatDate(row.consultation_date, 'yyyy-MM-dd') : NA),
        width: {minWidth: '230px'},
      },
      {
        name: 'amount',
        align: 'center',
        title: <Text>AMOUNT</Text>,
        render: row => `₹${row.amount}`,
        width: {minWidth: '200px'},
      },
      {
        name: 'payout_date',
        align: 'center',
        title: <Text>PAYOUT DATE</Text>,
        render: row => (row.payout_date ? formatDate(row.payout_date, 'yyyy-MM-dd') : NA),
        width: {minWidth: '230px'},
      },
    ],
    [],
  );

  return (
    <div className="min-h-screen bg-[#F9FAFB] p-4 sm:p-6 lg:p-8">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-FigtreeMeduim font-bold text-ui_blue mb-2">Settled Payments</h1>
        <p className="text-base sm:text-lg font-FigtreeRegular text-ui_input_border_dark">
          Funds will be credited to your bank in 2–5 business days after payout initiation
        </p>
      </div>
      <div className="my-5  grid grid-cols-2 sm:flex flex-wrap gap-3 z-[1]">
        <TextInput
          value={search}
          onChangeText={setSearch}
          placeholder="Search by case ID & Patient name"
          leftIcon="search_blue"
          wrapperClassName="pl-2 border"
          className="grow md:min-w-[240px] col-span-full md:max-w-[324px]"
        />
        <DateInput
          value={from}
          onChangeDate={setFrom}
          placeholder="From"
          labelClassName="!font-FigtreeSemiBold"
          wrapperClassName="border rounded-lg"
          className="sm:max-w-[150px] w-full sm:w-fit min-w-[100px]"
          textClassName="text-start text-ui_blue"
          maxValue={to ?? new Date()}
        />
        <DateInput
          value={to}
          onChangeDate={setTo}
          placeholder="To"
          labelClassName="!font-FigtreeSemiBold"
          wrapperClassName="border rounded-lg"
          textClassName="text-start text-ui_blue"
          className="sm:max-w-[150px] w-full sm:w-fit min-w-[100px]"
          minValue={from ?? undefined}
          maxValue={new Date()}
        />
        {isFilterActive && <Button title="Reset Filters" variant="secondary" onClick={resetFilter} />}
      </div>

      <DataTable
        columns={columns}
        data={data}
        totalPages={totalPages}
        currentPage={currentPage}
        totalResult={totalResult}
        onPageChange={setPage}
        loading={loading}
        pageSize={50}
        errorMessage={errorMessage}
      />
    </div>
  );
};
